import { FC, ReactNode } from 'react';

import { Badge } from '../../../components/ui/Badge';
import { Card, CardContent } from '../../../components/ui/Card';
import { cn } from '../../../utils';

export type FaqStep = {
    title: string;
    description: string;
};

type Props = {
    id: string;
    variant: 'buyer' | 'seller';
    icon: ReactNode;
    eyebrow: string;
    title: string;
    description: string;
    steps: FaqStep[];
};

const variantClasses = {
    buyer: {
        badge: 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900 dark:bg-blue-950/40 dark:text-blue-300',
        step: 'bg-blue-600 text-white dark:bg-blue-500',
    },
    seller: {
        badge: 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300',
        step: 'bg-emerald-600 text-white dark:bg-emerald-500',
    },
};

export const FaqJourney: FC<Props> = ({
    id,
    variant,
    icon,
    eyebrow,
    title,
    description,
    steps,
}) => {
    const colors = variantClasses[variant];

    return (
        <section
            id={id}
            className="scroll-mt-24"
            aria-labelledby={`${id}-title`}
        >
            <div>
                <Badge
                    variant="outline"
                    className={cn('gap-1.5 px-3 py-1', colors.badge)}
                >
                    {icon}
                    {eyebrow}
                </Badge>
                <h2 id={`${id}-title`} className="mt-4 text-3xl font-bold">
                    {title}
                </h2>
                <p className="mt-2 max-w-2xl text-muted-foreground">
                    {description}
                </p>
            </div>

            <ol className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {steps.map((step, index) => (
                    <li key={step.title}>
                        <Card className="h-full shadow-none">
                            <CardContent className="p-5">
                                <span
                                    className={cn(
                                        'flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold',
                                        colors.step,
                                    )}
                                    aria-hidden="true"
                                >
                                    {index + 1}
                                </span>
                                <h3 className="mt-4 font-semibold">
                                    {step.title}
                                </h3>
                                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                                    {step.description}
                                </p>
                            </CardContent>
                        </Card>
                    </li>
                ))}
            </ol>
        </section>
    );
};
